import { useCallback, useRef, useState } from "react"
import { Alert, Box, Button, Card, Chip, CircularProgress, Typography } from "@mui/material"
import { log } from "helpers/logger"
import { useScheduledWorkflow, useScheduledWorkflowLifecycle } from "resources/scheduled-workflows/hooks"
import type { ArchiveWorkflowDraft } from "resources/scheduled-workflows/draft-contract"
import { SCHEDULED_WORKFLOW_ERROR_CODES, isScheduledWorkflowNotFound, scheduledWorkflowErrorOf } from "resources/scheduled-workflows/errors"
import { NO_NOTIFICATION, WORKFLOW_GONE } from "./create-draft-failure"
import { ConfirmActionDialog } from "./confirm-action-dialog"
import { ACTIONS_SX, CARD_SX, HEAD_SX, NAME_SX } from "./card-styles"

type ArchiveFailure = "idNotFound" | "retryable"

export interface ArchiveDraftCardProps {
  draft: ArchiveWorkflowDraft
}

export function ArchiveDraftCard({ draft }: ArchiveDraftCardProps) {
  const { mutateAsync, isPending } = useScheduledWorkflowLifecycle("archive", NO_NOTIFICATION, false)
  const { data: workflow, error: readError } = useScheduledWorkflow(draft.scheduledWorkflowId)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isArchived, setIsArchived] = useState(false)
  const [failure, setFailure] = useState<ArchiveFailure | null>(null)
  const isRunning = useRef(false)
  const name = workflow?.name ?? "Workflow"
  const isDone = isArchived || workflow?.archivedAt != null
  const isMissing = failure === "idNotFound" || isScheduledWorkflowNotFound(readError)

  const openDialog = useCallback(() => setIsDialogOpen(true), [])
  const closeDialog = useCallback(() => setIsDialogOpen(false), [])

  const onConfirm = useCallback(async () => {
    if (isRunning.current) return
    isRunning.current = true
    setIsDialogOpen(false)
    setFailure(null)
    try {
      await mutateAsync(draft.scheduledWorkflowId)
      setIsArchived(true)
    } catch (error) {
      const workflowError = scheduledWorkflowErrorOf(error)
      if (workflowError?.code === SCHEDULED_WORKFLOW_ERROR_CODES.idNotFound) {
        setFailure("idNotFound")
        return
      }
      log(error)
      setFailure("retryable")
    } finally {
      isRunning.current = false
    }
  }, [mutateAsync, draft.scheduledWorkflowId])

  return (
    <Card variant="outlined" sx={CARD_SX} data-testid="kelly-workflow-draft">
      <Box sx={HEAD_SX}>
        <Typography sx={NAME_SX} data-testid="kelly-workflow-draft-name">{name}</Typography>
        <Chip size="small" variant="outlined" color="warning" label="Archives a workflow" data-testid="kelly-workflow-draft-action-chip" />
      </Box>
      <Typography variant="body2" color="text.secondary">Stops all future runs. Past reports stay in Reports.</Typography>
      {isDone && (
        <Alert severity="success" data-testid="kelly-workflow-draft-done">{`"${name}" is archived. Its reports stay in Reports.`}</Alert>
      )}
      {!isDone && isMissing && (
        <Alert severity="warning" data-testid="kelly-workflow-draft-id-not-found">{WORKFLOW_GONE}</Alert>
      )}
      {!isDone && !isMissing && (
        <>
          {failure === "retryable" && (
            <Alert severity="error" data-testid="kelly-workflow-draft-error">{`"${name}" couldn't be archived. Try again.`}</Alert>
          )}
          <Box sx={ACTIONS_SX}>
            <Button
              variant="contained"
              size="small"
              disabled={isPending}
              onClick={openDialog}
              data-testid="kelly-workflow-draft-confirm"
              startIcon={isPending ? <CircularProgress size={14} /> : undefined}
            >
              Archive workflow…
            </Button>
          </Box>
        </>
      )}
      <ConfirmActionDialog
        open={isDialogOpen}
        title={`Archive "${name}"?`}
        body="It will stop running. This can't be undone."
        confirmLabel="Archive"
        onClose={closeDialog}
        onConfirm={() => { void onConfirm() }}
      />
    </Card>
  )
}
